import type {
  CatalogVersionStateDto,
  ParsingStatusDto,
  SeniorityLevelDto,
  SkillStateDto,
  ValidationStatusDto,
} from '@/api/catalog-client';

// =============================================================================
// catalog-labels.ts — Mapping état de version ASCII (API) → libellé FR + variant.
// =============================================================================
// L'API porte un enum ASCII (DRAFT/PUBLISHED/ARCHIVED, contrat figé S8-D2) ; l'UI le
// rend en libellé FR accentué (i18n front). WCAG 2.1 AA : la COULEUR n'est JAMAIS le
// seul porteur d'information — chaque état rend un LIBELLÉ TEXTE ; le variant de badge
// n'est qu'un renfort visuel. Aucune règle métier ici : table de traduction pure +
// formatage de date présentationnel.

/** Variant de badge shadcn disponible (cf. components/ui/badge.tsx). */
export type BadgeVariant = 'default' | 'secondary' | 'outline' | 'accent';

interface LabelWithVariant {
  readonly label: string;
  readonly variant: BadgeVariant;
}

/** 3 états de version → libellés FR (contrat API figé S8-D2). */
const CATALOG_VERSION_STATE_LABELS: Record<
  CatalogVersionStateDto,
  LabelWithVariant
> = {
  DRAFT: { label: 'Brouillon', variant: 'outline' },
  PUBLISHED: { label: 'Publiée', variant: 'default' },
  ARCHIVED: { label: 'Archivée', variant: 'secondary' },
};

/** Libellé FR + variant d'un état de version de catalogue. */
export function catalogVersionStateLabel(
  state: CatalogVersionStateDto,
): LabelWithVariant {
  return CATALOG_VERSION_STATE_LABELS[state];
}

// =============================================================================
// S9-D4 — libellés d'écriture admin (compétences A-25, upload A-10, séniorité).
// =============================================================================

/** État d'une compétence (SkillStateDto) → libellé FR + variant. WCAG : libellé = porteur. */
const SKILL_STATE_LABELS: Record<SkillStateDto, LabelWithVariant> = {
  Active: { label: 'Active', variant: 'default' },
  Archived: { label: 'Archivée', variant: 'secondary' },
};

/** Libellé FR + variant d'un état de compétence. */
export function skillStateLabel(state: SkillStateDto): LabelWithVariant {
  return SKILL_STATE_LABELS[state];
}

/** Statut de parsing (ParsingStatusDto) → libellé FR. Présentation pure. */
const PARSING_STATUS_LABELS: Record<ParsingStatusDto, string> = {
  Pending: 'En attente',
  ParsingFailed: 'Lecture échouée',
  ParsingOk: 'Lecture réussie',
};

/** Libellé FR d'un statut de parsing d'upload. */
export function parsingStatusLabel(status: ParsingStatusDto): string {
  return PARSING_STATUS_LABELS[status];
}

/** Statut de validation sémantique (ValidationStatusDto) → libellé FR. Présentation pure. */
const VALIDATION_STATUS_LABELS: Record<ValidationStatusDto, string> = {
  NotStarted: 'Non démarrée',
  Failed: 'Échouée',
  Passed: 'Validée',
};

/** Libellé FR d'un statut de validation d'upload. */
export function validationStatusLabel(status: ValidationStatusDto): string {
  return VALIDATION_STATUS_LABELS[status];
}

/** Sévérité d'anomalie (blocking/warning) → libellé FR + variant. WCAG : libellé = porteur. */
export function anomalySeverityLabel(
  severity: 'blocking' | 'warning',
): LabelWithVariant {
  return severity === 'blocking'
    ? { label: 'Bloquante', variant: 'secondary' }
    : { label: 'Avertissement', variant: 'outline' };
}

/** Niveaux de séniorité acceptés (énumération fermée DDD §3.4.6) — ordre d'affichage. */
export const SENIORITY_LEVELS: readonly SeniorityLevelDto[] = [
  'Débutant',
  'Maîtrisant',
  'Expert',
  'Référent',
];

/**
 * Formate un horodatage ISO 8601 en date locale lisible (FR). `null` → tiret « — »
 * (état absent, p. ex. version jamais publiée). Fallback : la chaîne brute si elle
 * n'est pas une date valide. Présentation pure (aucune règle métier).
 */
export function formatCatalogDate(iso: string | null): string {
  if (iso === null) {
    return '—';
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('fr-FR', { dateStyle: 'medium' });
}
